'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';

interface DiagnosticTimerProps {
  totalSeconds: number;
  onExpire: () => void;
}

export default function DiagnosticTimer({ totalSeconds, onExpire }: DiagnosticTimerProps) {
  const [remaining, setRemaining] = useState(totalSeconds);
  const expiredRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!expiredRef.current) {
            expiredRef.current = true;
            onExpire();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onExpire]);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const isWarning = remaining <= 600 && remaining > 300;
  const isDanger = remaining <= 300;

  const timerClass = isDanger ? 'timer-danger' : isWarning ? 'timer-warning' : 'text-foreground';

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${isDanger ? 'border-danger/40 bg-danger-bg' : isWarning ? 'border-warning/40 bg-warning-bg' : 'border-border bg-muted'}`}>
      <Clock size={14} className={timerClass} />
      <span className={`text-sm font-bold tabular-nums font-mono ${timerClass}`}>
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
      {isWarning && !isDanger && (
        <span className="text-xs text-warning font-medium">Low time</span>
      )}
      {isDanger && (
        <span className="text-xs text-danger font-semibold">Final minutes!</span>
      )}
    </div>
  );
}